import { writable, get } from 'svelte/store';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/sveltekit';
import { browser } from '$app/environment';
// Design Ref: §2.3 — 순환 의존(session.js ↔ authStore.js)은 method body에서만 참조하므로 안전
import * as session from '$lib/auth/session.js';

// Design Ref: §2.2, §6.1 — PostHog/Sentry user 식별 단일 진입점. account_code 누락 시 graceful skip
function identifyUser(user) {
	if (!browser) return;
	const code = user?.account_code;
	if (!code) {
		try {
			Sentry.captureMessage('identify_missing_account_code', 'warning');
		} catch {
			// SDK 호출 실패는 swallow — 식별 누락이 앱 동작을 막지 않도록 함
		}
		return;
	}
	try {
		posthog.identify(code);
		Sentry.setUser({ id: code });
	} catch {
		// SDK 호출 실패는 swallow — 식별 누락이 앱 동작을 막지 않도록 함
	}
}

// Design Ref: §2.2 — logout 시 식별 누수 차단 (같은 브라우저 다른 계정 로그인 대비)
function resetIdentity() {
	if (!browser) return;
	try {
		posthog.reset();
		Sentry.setUser(null);
	} catch {
		// SDK 호출 실패는 swallow — 식별 누락이 앱 동작을 막지 않도록 함
	}
}

function createAuthStore() {
	const { subscribe, update, set } = writable({
		isInitialized: false,
		isLoggedIn: false,
		user: null
	});

	return {
		subscribe,
		// Plan FR-06 / Design §2.1 — 호출자가 login 시 user 정보만 전달
		login: (user) => {
			update((state) => ({
				...state,
				isLoggedIn: true,
				user
			}));
			identifyUser(user);
		},
		// Design §2.1 — 부수효과는 session에 위임 (백엔드 호출/localStorage 정리/리다이렉트)
		logout: async () => {
			await session.logout();
		},
		setUser: (user) =>
			update((state) => ({
				...state,
				user
			})),
		// Design §2.1 — bootstrap이 마이그레이션·refresh·profile을 모두 책임
		init: async () => {
			if (!browser) return;
			await session.bootstrap();
		},
		// 초기화 완료까지 대기하는 헬퍼 — bootstrap이 idempotent라 그대로 위임
		waitForInit: async () => {
			if (!browser) return;
			const state = get({ subscribe });
			if (state.isInitialized) return;
			await session.bootstrap();
		},
		// session에서 호출하는 내부 setter들 — 외부 호출 금지 (밑줄 명명 대신 명시적 메서드명)
		setUnauthenticated: () => {
			set({ isInitialized: true, isLoggedIn: false, user: null });
			resetIdentity();
		},
		markInitialized: () =>
			update((state) => ({
				...state,
				isInitialized: true
			}))
	};
}

export const authStore = createAuthStore();
